import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import getAPIbaseURL from "../../utils/helpers";

const API_URL = getAPIbaseURL();
const FOLDERS_URL = `${API_URL}/organization/documents/folders`;
const DOCUMENTS_URL = `${API_URL}/organization/documents/documents`;
const employeeUrl = (userId) => `${API_URL}/users/${userId}/documents`;

const authConfig = (extra = {}) => ({
  ...extra,
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    ...extra.headers,
  },
});

const errorValue = (error, fallback) =>
  error.response?.data?.detail || error.response?.data?.error || fallback;

const saveDownload = (response) => {
  const disposition = response.headers["content-disposition"] || "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/)?.[1];
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = encoded ? decodeURIComponent(encoded) : "download";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Organization settings (super admin)

export const fetchOrganizationFolders = createAsyncThunk(
  "organizationDocuments/fetchFolders",
  async (scope, thunkAPI) => {
    try {
      return (await axios.get(FOLDERS_URL, authConfig({ params: scope ? { scope } : {} }))).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to load folders"));
    }
  },
);

export const fetchGrantTargets = createAsyncThunk(
  "organizationDocuments/fetchGrantTargets",
  async (_, thunkAPI) => {
    try {
      return (await axios.get(`${API_URL}/organization/documents/grant-targets`, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to load people and groups"));
    }
  },
);

export const createOrganizationFolder = createAsyncThunk(
  "organizationDocuments/createFolder",
  async (payload, thunkAPI) => {
    try {
      return (await axios.post(FOLDERS_URL, payload, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to create folder"));
    }
  },
);

export const updateOrganizationFolder = createAsyncThunk(
  "organizationDocuments/updateFolder",
  async ({ folderId, updates }, thunkAPI) => {
    try {
      return (await axios.patch(`${FOLDERS_URL}/${folderId}`, updates, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to update folder"));
    }
  },
);

export const replaceOrganizationFolderGrants = createAsyncThunk(
  "organizationDocuments/replaceGrants",
  async ({ folderId, grants }, thunkAPI) => {
    try {
      return (await axios.put(`${FOLDERS_URL}/${folderId}/grants`, grants, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to update access"));
    }
  },
);

export const deleteOrganizationFolder = createAsyncThunk(
  "organizationDocuments/deleteFolder",
  async (folderId, thunkAPI) => {
    try {
      await axios.delete(`${FOLDERS_URL}/${folderId}`, authConfig());
      return folderId;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to delete folder"));
    }
  },
);

export const fetchDeletedItems = createAsyncThunk(
  "organizationDocuments/fetchDeletedItems",
  async (_, thunkAPI) => {
    try {
      return (await axios.get(`${API_URL}/organization/documents/deleted`, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to load deleted items"));
    }
  },
);

export const restoreOrganizationFolder = createAsyncThunk(
  "organizationDocuments/restoreFolder",
  async (folderId, thunkAPI) => {
    try {
      return (await axios.post(`${FOLDERS_URL}/${folderId}/restore`, null, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to restore folder"));
    }
  },
);

export const restoreDeletedDocument = createAsyncThunk(
  "organizationDocuments/restoreDocument",
  async (documentId, thunkAPI) => {
    try {
      await axios.post(`${API_URL}/organization/documents/deleted/documents/${documentId}/restore`, null, authConfig());
      return documentId;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to restore file"));
    }
  },
);

// Folder contents managed from organization settings. `employeeId` targets one employee's copy of an
// employee folder; leaving it empty targets the documents shared with every employee (or the company folder).

export const fetchFolderDocuments = createAsyncThunk(
  "organizationDocuments/fetchFolderDocuments",
  async ({ folderId, employeeId }, thunkAPI) => {
    try {
      const params = employeeId ? { employee_id: employeeId } : {};
      return (await axios.get(`${FOLDERS_URL}/${folderId}/documents`, authConfig({ params }))).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to load files"));
    }
  },
);

export const uploadFolderDocument = createAsyncThunk(
  "organizationDocuments/uploadFolderDocument",
  async ({ folderId, employeeId, file }, thunkAPI) => {
    const body = new FormData();
    body.append("title", file.name);
    body.append("file", file);
    if (employeeId) body.append("employee_id", employeeId);
    try {
      return (await axios.post(`${FOLDERS_URL}/${folderId}/documents`, body, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to upload file"));
    }
  },
);

export const uploadOrganizationDocumentVersion = createAsyncThunk(
  "organizationDocuments/uploadDocumentVersion",
  async ({ documentId, file }, thunkAPI) => {
    const body = new FormData();
    body.append("file", file);
    try {
      return (await axios.post(`${DOCUMENTS_URL}/${documentId}/versions`, body, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to upload version"));
    }
  },
);

export const deleteOrganizationDocument = createAsyncThunk(
  "organizationDocuments/deleteDocument",
  async (documentId, thunkAPI) => {
    try {
      await axios.delete(`${DOCUMENTS_URL}/${documentId}`, authConfig());
      return documentId;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to delete file"));
    }
  },
);

export const downloadOrganizationDocument = createAsyncThunk(
  "organizationDocuments/downloadDocument",
  async ({ documentId, versionId }, thunkAPI) => {
    try {
      saveDownload(await axios.get(`${DOCUMENTS_URL}/${documentId}/download`, authConfig({
        params: versionId ? { version_id: versionId } : {},
        responseType: "blob",
      })));
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to download file"));
    }
  },
);

export const fetchOrganizationDocumentVersions = createAsyncThunk(
  "organizationDocuments/fetchDocumentVersions",
  async (documentId, thunkAPI) => {
    try {
      const response = await axios.get(`${DOCUMENTS_URL}/${documentId}/versions`, authConfig());
      return { documentId, versions: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to load version history"));
    }
  },
);

// Employee profile documents

export const fetchEmployeeFolders = createAsyncThunk(
  "organizationDocuments/fetchEmployeeFolders",
  async (userId, thunkAPI) => {
    try {
      return (await axios.get(`${employeeUrl(userId)}/folders`, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        notFound: error.response?.status === 404,
        message: errorValue(error, "Unable to load documents"),
      });
    }
  },
);

export const fetchEmployeeFolder = createAsyncThunk(
  "organizationDocuments/fetchEmployeeFolder",
  async ({ userId, folderId }, thunkAPI) => {
    try {
      return (await axios.get(`${employeeUrl(userId)}/folders/${folderId}`, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ message: errorValue(error, "Unable to load documents") });
    }
  },
);

export const uploadEmployeeDocument = createAsyncThunk(
  "organizationDocuments/uploadEmployeeDocument",
  async ({ userId, folderId, file }, thunkAPI) => {
    const body = new FormData();
    body.append("title", file.name);
    body.append("file", file);
    try {
      return (await axios.post(`${employeeUrl(userId)}/folders/${folderId}`, body, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ message: errorValue(error, "Unable to upload file") });
    }
  },
);

export const uploadEmployeeDocumentVersion = createAsyncThunk(
  "organizationDocuments/uploadEmployeeDocumentVersion",
  async ({ userId, documentId, file }, thunkAPI) => {
    const body = new FormData();
    body.append("file", file);
    try {
      return (await axios.post(`${employeeUrl(userId)}/${documentId}/versions`, body, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ message: errorValue(error, "Unable to upload version") });
    }
  },
);

export const deleteEmployeeDocument = createAsyncThunk(
  "organizationDocuments/deleteEmployeeDocument",
  async ({ userId, documentId }, thunkAPI) => {
    try {
      await axios.delete(`${employeeUrl(userId)}/${documentId}`, authConfig());
      return documentId;
    } catch (error) {
      return thunkAPI.rejectWithValue({ message: errorValue(error, "Unable to delete file") });
    }
  },
);

export const fetchEmployeeDocumentVersions = createAsyncThunk(
  "organizationDocuments/fetchEmployeeDocumentVersions",
  async ({ userId, documentId }, thunkAPI) => {
    try {
      const response = await axios.get(`${employeeUrl(userId)}/${documentId}/versions`, authConfig());
      return { documentId, versions: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue({ message: errorValue(error, "Unable to load version history") });
    }
  },
);

export const downloadEmployeeDocument = createAsyncThunk(
  "organizationDocuments/downloadEmployeeDocument",
  async ({ userId, documentId, versionId }, thunkAPI) => {
    try {
      saveDownload(await axios.get(
        `${employeeUrl(userId)}/${documentId}/download`,
        authConfig({ params: versionId ? { version_id: versionId } : {}, responseType: "blob" }),
      ));
    } catch (error) {
      return thunkAPI.rejectWithValue({ message: errorValue(error, "Unable to download file") });
    }
  },
);

const initialState = {
  folders: [],
  grantTargets: { users: [], teams: [], delegations: [], roles: [] },
  isLoading: false,
  error: null,
  deletedItems: { folders: [], documents: [] },
  deletedLoading: false,
  folderDocuments: [],
  folderDocumentsLoading: false,
  versionsByDocument: {},
  employeeFolders: [],
  employeeFolder: null,
  employeeDocuments: [],
  employeeLoading: false,
  employeeError: null,
  employeeAccessDenied: false,
};

const upsertFolder = (state, folder) => {
  const index = state.folders.findIndex((item) => item.id === folder.id);
  if (index === -1) state.folders.push(folder);
  else state.folders[index] = folder;
};

const organizationDocumentsSlice = createSlice({
  name: "organizationDocuments",
  initialState,
  reducers: {
    clearOrganizationDocumentsError(state) {
      state.error = null;
      state.employeeError = null;
    },
    closeEmployeeFolder(state) {
      state.employeeFolder = null;
      state.employeeDocuments = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationFolders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationFolders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.folders = action.payload;
      })
      .addCase(fetchOrganizationFolders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchGrantTargets.fulfilled, (state, action) => {
        state.grantTargets = action.payload;
      })
      .addCase(createOrganizationFolder.fulfilled, (state, action) => upsertFolder(state, action.payload))
      .addCase(updateOrganizationFolder.fulfilled, (state, action) => upsertFolder(state, action.payload))
      .addCase(replaceOrganizationFolderGrants.fulfilled, (state, action) => upsertFolder(state, action.payload))
      .addCase(deleteOrganizationFolder.fulfilled, (state, action) => {
        state.folders = state.folders.filter((folder) => folder.id !== action.payload);
      })
      .addCase(fetchFolderDocuments.pending, (state) => {
        state.folderDocumentsLoading = true;
        state.error = null;
      })
      .addCase(fetchFolderDocuments.fulfilled, (state, action) => {
        state.folderDocumentsLoading = false;
        state.folderDocuments = action.payload;
      })
      .addCase(fetchFolderDocuments.rejected, (state) => {
        state.folderDocumentsLoading = false;
        state.folderDocuments = [];
      })
      .addCase(fetchOrganizationDocumentVersions.fulfilled, (state, action) => {
        state.versionsByDocument[action.payload.documentId] = action.payload.versions;
      })
      .addCase(fetchEmployeeDocumentVersions.fulfilled, (state, action) => {
        state.versionsByDocument[action.payload.documentId] = action.payload.versions;
      })
      .addCase(deleteOrganizationDocument.fulfilled, (state, action) => {
        state.folderDocuments = state.folderDocuments.filter((document) => document.id !== action.payload);
      })
      .addCase(fetchDeletedItems.pending, (state) => {
        state.deletedLoading = true;
        state.error = null;
      })
      .addCase(fetchDeletedItems.fulfilled, (state, action) => {
        state.deletedLoading = false;
        state.deletedItems = action.payload;
      })
      .addCase(fetchDeletedItems.rejected, (state) => {
        state.deletedLoading = false;
      })
      .addCase(restoreOrganizationFolder.fulfilled, (state, action) => {
        upsertFolder(state, action.payload);
        state.deletedItems.folders = state.deletedItems.folders.filter((folder) => folder.id !== action.payload.id);
      })
      .addCase(restoreDeletedDocument.fulfilled, (state, action) => {
        state.deletedItems.documents = state.deletedItems.documents.filter((document) => document.id !== action.payload);
      })
      .addCase(fetchEmployeeFolders.pending, (state) => {
        state.employeeLoading = true;
        state.employeeError = null;
        state.employeeAccessDenied = false;
      })
      .addCase(fetchEmployeeFolders.fulfilled, (state, action) => {
        state.employeeLoading = false;
        state.employeeFolders = action.payload;
      })
      .addCase(fetchEmployeeFolders.rejected, (state, action) => {
        state.employeeLoading = false;
        state.employeeFolders = [];
        state.employeeAccessDenied = Boolean(action.payload?.notFound);
        if (!action.payload?.notFound) state.employeeError = action.payload?.message;
      })
      .addCase(fetchEmployeeFolder.pending, (state) => {
        state.employeeLoading = true;
        state.employeeError = null;
      })
      .addCase(fetchEmployeeFolder.fulfilled, (state, action) => {
        state.employeeLoading = false;
        state.employeeFolder = action.payload.folder;
        state.employeeDocuments = action.payload.documents;
      })
      .addCase(fetchEmployeeFolder.rejected, (state, action) => {
        state.employeeLoading = false;
        state.employeeError = action.payload?.message;
      })
      .addCase(deleteEmployeeDocument.fulfilled, (state, action) => {
        state.employeeDocuments = state.employeeDocuments.filter((document) => document.id !== action.payload);
      })
      .addMatcher(
        (action) =>
          action.type.startsWith("organizationDocuments/") &&
          action.type.endsWith("/rejected") &&
          !action.type.includes("Employee"),
        (state, action) => {
          state.error = action.payload;
        },
      )
      .addMatcher(
        (action) =>
          /organizationDocuments\/(upload|delete|download)Employee/.test(action.type) &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.employeeError = action.payload?.message;
        },
      );
  },
});

export const { clearOrganizationDocumentsError, closeEmployeeFolder } = organizationDocumentsSlice.actions;
export default organizationDocumentsSlice.reducer;
